import { differenceInDays, fromUnixTime } from "date-fns"
import { observer } from "mobx-react-lite"

import type { Token } from "@/domains/core"
import type { VenammSellStore } from "@/domains/venamm"
import type { IVenammStore } from "@/domains/venamm"
import { Body3Xl } from "@/domains/design/components/body-3xl"
import { BodyCaption } from "@/domains/design/components/body-caption"
import { BodySM } from "@/domains/design/components/body-sm"
import { formatCurrency } from "@/domains/library"

import { VenammCurveChart } from "./venamm-curve-chart"

interface VenammQuoteReadyProps {
  store: VenammSellStore
  governanceToken: Token
  venammStore: IVenammStore
}

export const VenammQuoteReady = observer(({ store, governanceToken, venammStore }: VenammQuoteReadyProps) => {
  if (!store.lock) {
    return <div className="text-t300">No lock selected</div>
  }

  if (!store.quote) {
    return (
      <div className="text-t300">
        Quote not available. Please wait... (State: {store.state}, Lock: {store.lock?.id})
      </div>
    )
  }

  const now = new Date()
  const expiryDate = fromUnixTime(store.lock.lockEnds)
  const expiryDays = differenceInDays(expiryDate, now)
  const quoteRate = store.lock.lockAmount > 0 ? store.quote.value / store.lock.lockAmount : 0

  return (
    <>
      <div className="h-120 relative mb-6 flex min-h-60 flex-col justify-between gap-4 md:h-60 md:flex-row">
        <div className="h-full w-full flex-grow md:w-1/2">
          <Quote store={store} governanceToken={governanceToken} quoteRate={quoteRate} expiryDays={expiryDays} />
        </div>
        <div className="h-60 w-full md:w-1/2">
          <VenammCurveChart quote={store.quote} lock={store.lock} />
        </div>
      </div>

      <BodyCaption As="div" className="text-t100">
        {formatCurrency(venammStore.availableTokenSupplyValue, 2)}{" "}
        <span className="text-t300">{governanceToken.symbol}</span> available in vault
      </BodyCaption>
    </>
  )
})

const Quote = ({
  store,
  governanceToken,
  quoteRate,
  expiryDays,
}: {
  store: VenammSellStore
  governanceToken: Token
  quoteRate: number
  expiryDays: number
}) => {
  if (!store.quote || !store.lock) {
    return null
  }

  return (
    <div className="hover:bg-brightness-150 flex h-full flex-grow flex-col justify-between gap-4 rounded-2xl border border-p200-shade p-8 text-center text-white">
      <BodyCaption As="div" className="text-t300">
        Your quote @ rate {formatCurrency(quoteRate, 3)}
      </BodyCaption>
      <div>
        <BodySM As="div" className="text-t300">
          You will receive
        </BodySM>
        <Body3Xl As="div" className="text-t100">
          {formatCurrency(store.quote.value, 4)} <span className="text-t100">{governanceToken.symbol}</span>
        </Body3Xl>
      </div>
      {expiryDays > 0 ? (
        <BodyCaption As="div" className="text-t300">
          Can be redeemed {expiryDays} days early.
        </BodyCaption>
      ) : null}
      {expiryDays <= 0 ? (
        <BodyCaption As="div" className="text-t300">
          This NFT expired on {store.lock.displayLockEnds}
        </BodyCaption>
      ) : null}
    </div>
  )
}
